<template>
  <v-row class="pa-6 ma-0">
    <!-- ! Left Side -->
    <v-col cols="12" sm="6" md="6" :class="$vuetify.display.xs ? 'pl-4 pr-4' : 'pl-4 pr-4'">
      <!-- ! Coverage Header with tooltip -->
      <v-row>
        <v-col cols="8" class="mt-4 pb-2 font-weight-bold underline_bottom">
          {{ $t('L_coverage') }}
          <v-tooltip
            content-class="text-center"
            location="bottom"
            :max-width="$vuetify.display.mdAndUp ? '194' : '162'"
          >
            <template #activator="{ props }">
              <v-icon
                v-bind="props"
                tabindex="0"
                color="primary"
                size="x-small"
                class="cursor-pointer"
                style="margin-top: -6px"
                aria-label="coverage amount information"
                :icon="mdiHelpCircle"
              />
            </template>
            <span role="tooltip" aria-live="polite" id="policy_number_tooltip">
              {{ __t('L_coverage_amount_tooltip_dashboard') }}
            </span>
          </v-tooltip>
        </v-col>
        <v-col
          cols="4"
          class="mt-4 pb-2 text-right font-weight-bold underline_bottom"
          :class="$vuetify.display.xs ? 'font-size--14' : 'font-size--16'"
        >
          &nbsp;
        </v-col>
      </v-row>
      <!-- ! Primary -->
      <v-row v-if="$vuetify.display.smAndUp || showCoverageDetails">
        <v-col class="text-left py-1">{{ primaryName }}</v-col>
        <v-col
          class="text-right py-1"
          :class="$vuetify.display.xs ? 'font-size--14  pr-4' : 'font-size--16'"
        >
          {{ formattedPrimaryCoverage }}
        </v-col>
      </v-row>
      <!-- ! Spouse-->
      <v-row v-if="($vuetify.display.smAndUp || showCoverageDetails) && spouseName !== ''">
        <v-col class="text-left py-1">{{ spouseName }}</v-col>
        <v-col
          class="text-right py-1"
          :class="$vuetify.display.xs ? 'font-size--14 pr-4' : 'font-size--16'"
        >
          {{ formattedSpouseCoverage }}
        </v-col>
      </v-row>
      <!-- ! Children-->
      <v-row v-if="($vuetify.display.smAndUp || showCoverageDetails) && hasChildren">
        <v-col class="text-left py-1">{{ $t('L_children') }}</v-col>
        <v-col
          class="text-right py-1"
          :class="$vuetify.display.xs ? 'font-size--14 pr-4' : 'font-size--16'"
        >
          {{ formattedChildrenCoverage }}
        </v-col>
      </v-row>
    </v-col>
    <!-- ! Right Side -->
    <v-col
      cols="12"
      sm="6"
      md="6"
      class="mt-4"
      :class="$vuetify.display.xs ? 'pl-0 pr-0' : 'pl-4 pr-4'"
      v-if="!isPending"
    >
      <v-row class="blueBackground pa-6 mobile-layout-select-beneficiary">
        <v-col
          :class="$vuetify.display.xs ? 'text-center' : 'text-center'"
          class="flex-column pa-0"
        >
          <div class="d-flex flex-end flex-column">
            <h4 class="font-bold text-left">{{ $t('L_need_to_make_an_update_to_this_policy') }}</h4>
            <OnexSelect
              :items="updateTypeList"
              :label="$t('')"
              v-model="updateType"
              class="selectMaxWidth"
              :menu-props="{
                maxHeight: '272px',
                location: 'bottom center',
                scrollStrategy: 'close',
              }"
            />
          </div>
          <OnexButton
            role="link"
            size="small"
            class="mobile_go_button_mt"
            color="primary"
            @click="navigateToPolicyDetails"
          >
            {{ $t('L_go') }}
          </OnexButton>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import { mapActions, mapStores } from 'pinia';
import { defineComponent, type ComponentOptions } from 'vue';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import type { IPolicyStorePolicy } from '@/modules/policy/store/policyStore.model';
import { mdiHelpCircle } from '@mdi/js';
import { currency } from '@/shared/util/formatters';
import { OnexButton, OnexSelect } from '@primerica/onex-ui-lib';
import { useGlobalStore } from '@/stores/global/global.store';
import { RouteNames } from '@/router/routeNames';

const componentOptions: ComponentOptions = {
  name: 'TermLifeCardCoverageSection',
  components: {
    OnexButton,
    OnexSelect,
  },
  props: {
    indexPolicy: {
      type: Number,
      required: true,
      default: 0,
    },
  },
  data: () => ({
    updateType: 0,
    showCoverageDetails: true,
    mdiHelpCircle,
  }),
  computed: {
    ...mapStores(usePolicyStore, useGlobalStore),
    selectedPolicy(): IPolicyStorePolicy {
      return this.policyStore.policies[this.indexPolicy];
    },
    isPending() {
      return this.selectedPolicy?.pending;
    },
    policyData() {
      return this.policyStore.policies[this.indexPolicy];
    },
    primaryName() {
      if (this.policyData) {
        return this.policyData?.insuredName;
      }
      return '';
    },
    formattedPrimaryCoverage() {
      return currency(this.policyData?.primaryCoverage, 0);
    },
    formattedSpouseCoverage() {
      return currency(this.policyData?.spouseCoverage, 0);
    },
    formattedChildrenCoverage() {
      return currency(this.policyData?.childCoverage, 0);
    },
    spouseName() {
      if (this.policyData?.data?.spouseFirstName) {
        return `${this.policyData?.data?.spouseFirstName} ${this.policyData?.data?.spouseLastName}`;
      }
      return '';
    },
    hasChildren() {
      return !!(this.policyData?.data?.children && this.policyData?.data?.children.length > 0);
    },
    updateTypeList() {
      const selectOptions = [
        {
          value: 0,
          title: this.$t('L_i_want_to_update_my'),
        },
        {
          value: 1,
          title: this.$t('L_beneficiaries'),
        },
      ];
      if (this.selectedPolicy?.billingForm === 'B2') {
        selectOptions.push({
          value: 2,
          title: this.$t('DropDown_L_bank_account'),
        });
        selectOptions.push({
          value: 3,
          title: this.$t('L_draft_date'),
        });
      }
      selectOptions.push({
        value: 4,
        title: this.$t('L_contact_information'),
      });
      selectOptions.push({
        value: 5,
        title: this.$t('L_billing_method'),
      });
      // Add Child - CRC
      if (this.selectedPolicy?.showAddChildLink) {
        selectOptions.push({
          value: 6,
          title: this.$t('L_add_child'),
        });
      }
      return selectOptions;
    },
  },
  methods: {
    ...mapActions(usePolicyStore, {
      setChangeBankAccountStep: 'setChangeBankAccountStep',
      setPayMethodChange: 'setPayMethodChange',
    }),
    __t(key: string): string {
      return this.$t(key + (this.policyStore.isPending ? '_application' : ''));
    },
    async navigateToPolicyDetails() {
      if (this.updateType > 0) {
        let scrollLocation = '';
        if (this.updateType === 1) {
          scrollLocation = 'beneficiary';
        } else if (this.updateType === 2 || this.updateType === 3 || this.updateType === 5) {
          scrollLocation = 'payment';
          if (this.updateType === 2) {
            // await this.globalStore.postAuditLog({ actionId: 'ChangeBankAccount_StartAction', details: `Policy Number: ${this.policyList[this.indexPolicy].contractId}` });
            this.setChangeBankAccountStep(1);
            this.$router.push({
              name: RouteNames.CHANGEBANKACCOUNTSTEP,
              params: { contractId: this.indexPolicy },
            });
            return;
          } else if (this.updateType === 5) {
            // await this.globalStore.postAuditLog({ actionId: 'UpdatePaymentMethod_StartAction', details: `Policy Number: ${this.policyList[this.indexPolicy].contractId}` });
            this.setPayMethodChange(1);
            this.$router.push({
              name: RouteNames.PAYMETHODCHANGE,
              params: { contractId: this.indexPolicy },
            });
            return;
          }
        } else if (this.updateType === 4) {
          scrollLocation = 'contact_info';
        } else if (this.updateType === 6) {
          scrollLocation = 'add_child';
        }
        this.policyStore.scrollToPolicyDetails = scrollLocation;
        await this.setPolicyIndex();
        this.$router.push({
          name: RouteNames.POLICY_DETAILS,
          params: { contractId: this.indexPolicy },
        });
      }
    },
    async setPolicyIndex() {
      await this.policyStore.changePolicy({ selectedIndex: this.indexPolicy });
      await this.policyStore.getPaymentInfo(
        this.policyStore.policies[this.indexPolicy]?.contractId
      );
    },
  },
};

export default defineComponent(componentOptions);
</script>

<style scoped>
.blueBackground {
  background-color: #f5f7fa;
}

.underline_bottom {
  border-bottom: 2px solid #adadad;
}

.selectMaxWidth {
  width: 100%;
  padding-bottom: 4px !important;
}

.mobile_go_button_mt {
  margin-top: 0px;
  width: 180px;
  height: 32px !important;
  margin-right: 0px;
  margin-left: 0px;
  padding-left: 0px !important;
  padding-right: 0px !important;

  @media only screen and (max-width: 760px) {
    margin: 0 auto !important;
  }
}

.mobile-layout-select-beneficiary {
  flex-direction: column;
}
</style>
